import { c as _c } from "react/compiler-runtime";
import { TeamIcon, Text, ToggleGroup } from "@flat-ui";
import { Stack } from "@mui-wrapper";
import { getProjectAccessSource } from "@utils/data-display";
import { getPrettyName } from "@utils/user-utils";

/** Which projects of a group to list, by where the member's access comes from */

/** Counts of a group's projects by where the member's access comes from */

/** Counts the projects by access source; a team outranking a direct role counts as both */
export function countProjectsByAccess(projects) {
  const sources = projects.map(getProjectAccessSource);
  return {
    direct: sources.filter(s => s === "direct" || s === "teamOverDirect").length,
    team: sources.filter(s => s === "team" || s === "teamOverDirect").length
  };
}

/**
 * Whether the projects mix direct and team access. Only then is the filter
 * offered: with a single source one of its options would always list nothing.
 */
export function hasMixedAccessSources(projects) {
  const counts = countProjectsByAccess(projects);
  return counts.direct > 0 && counts.team > 0;
}

/** Keeps the projects that match the access filter */
export function filterProjectsByAccess(projects, filter) {
  if (filter === "all") {
    return projects;
  }
  return projects.filter(project => {
    const source = getProjectAccessSource(project);
    return source === "teamOverDirect" || source === filter;
  });
}

/** The heading names the single access source when there is only one */
function ProjectsHeading(t0) {
  const $ = _c(10);
  const {
    member,
    counts
  } = t0;
  let t1;
  if ($[0] !== member) {
    t1 = getPrettyName(member);
    $[0] = member;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <var>{t1}</var>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const name = t2;
  if (counts.team === 0) {
    let t3;
    if ($[4] !== name) {
      t3 = <>Projects assigned directly to {name}</>;
      $[4] = name;
      $[5] = t3;
    } else {
      t3 = $[5];
    }
    return t3;
  }
  if (counts.direct === 0) {
    let t3;
    if ($[6] !== name) {
      t3 = <>Projects {name} is in through a team</>;
      $[6] = name;
      $[7] = t3;
    } else {
      t3 = $[7];
    }
    return t3;
  }
  let t3;
  if ($[8] !== name) {
    t3 = <>Projects assigned to {name} or one of their teams</>;
    $[8] = name;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
}
/**
 * Header of a group's projects on a member profile: what the list holds and,
 * when access comes from both direct assignments and teams, a filter between them.
 */
export function MemberGroupProjectsHeader(t0) {
  const $ = _c(26);
  const {
    member,
    projects,
    filter,
    onFilterChange
  } = t0;
  let t1;
  if ($[0] !== projects) {
    t1 = countProjectsByAccess(projects);
    $[0] = projects;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const counts = t1;
  const t2 = `All (${projects.length})`;
  let t3;
  if ($[2] !== t2) {
    t3 = {
      value: "all",
      label: t2
    };
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = `Direct (${counts.direct})`;
  let t5;
  if ($[4] !== t4) {
    t5 = {
      value: "direct",
      label: t4
    };
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const t6 = `Through a team (${counts.team})`;
  let t7;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <TeamIcon />;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  let t8;
  if ($[7] !== t6) {
    t8 = {
      value: "team",
      label: t6,
      icon: t7
    };
    $[7] = t6;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t9;
  if ($[9] !== t3 || $[10] !== t5 || $[11] !== t8) {
    t9 = [t3, t5, t8];
    $[9] = t3;
    $[10] = t5;
    $[11] = t8;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  const items = t9;
  let t10;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      alignItems: "center",
      justifyContent: "space-between",
      flexWrap: "wrap",
      gap: 1,
      px: 1.5,
      py: 1.5
    };
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      color: "text.secondary"
    };
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[15] !== counts || $[16] !== member) {
    t12 = <Text variant="bodyS" sx={t11}><ProjectsHeading member={member} counts={counts} /></Text>;
    $[15] = counts;
    $[16] = member;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  let t13;
  if ($[18] !== filter || $[19] !== items || $[20] !== onFilterChange || $[21] !== projects) {
    t13 = hasMixedAccessSources(projects) && <ToggleGroup size="small" value={filter} onChange={onFilterChange} items={items} />;
    $[18] = filter;
    $[19] = items;
    $[20] = onFilterChange;
    $[21] = projects;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] !== t12 || $[24] !== t13) {
    t14 = <Stack direction="row" sx={t10}>{t12}{t13}</Stack>;
    $[23] = t12;
    $[24] = t13;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  return t14;
}